import React from "react"
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { ApolloError } from "@apollo/client"
import { theme } from "../../theme"
import {
  ChartContainer,
  ChartLabel,
  ChartTooltip,
  ChartValue,
  ChartWrapper,
  EmptyState,
  Header,
  LoadingSkeleton,
  SkeletonAreaPath,
  SkeletonAxis,
  SkeletonLabel,
  Title,
  Wrapper
} from "./CheckoutRateChart.styled"
import { ChartTimeSeriesData } from "./useChartData"
import { ToggleButton, ToggleContainer } from "../DashboardSales/OrdersOverTimeChart.styled"
import dayjs from "dayjs"

interface CheckoutRateChartProps {
  chartData: ChartTimeSeriesData[]
  loading: boolean
  error?: ApolloError
  interval: "hourly" | "daily"
  onIntervalChange: (interval: "hourly" | "daily") => void
  startDate: string
  endDate: string
}

export const CheckoutRateChart: React.FC<CheckoutRateChartProps> = ({
  chartData,
  loading,
  error,
  interval,
  onIntervalChange,
  startDate,
  endDate
}) => {
  const timeRangeDays = dayjs(endDate).diff(startDate, "day")
  const getCheckoutRate = (orders: number, checkouts: number): number => {
    if (checkouts === 0) return 0
    return Number(((orders / checkouts) * 100).toFixed(1))
  }

  const chartDataWithRate = chartData.map((data) => ({
    ...data,
    checkoutRate: getCheckoutRate(data.orders, data.checkouts)
  }))

  const CustomTooltip = ({
    active,
    payload
  }: {
    active?: boolean
    payload?: { value: number; payload: ChartTimeSeriesData & { checkoutRate: number } }[]
  }) => {
    if (active && payload && payload.length) {
      if (
        payload[0].payload.checkouts === 0 &&
        payload[0].payload.sessions === 0 &&
        payload[0].payload.orders === 0
      )
        return null
      const { displayLabel } = payload[0].payload
      const rate = payload[0].value
      return (
        <ChartTooltip
        >
          <ChartLabel>{displayLabel}</ChartLabel>
          <ChartValue>
            Checkout Rate: {rate}%
          </ChartValue>
        </ChartTooltip>
      )
    }
    return null
  }

  const renderSkeletonChart = () => {
    const points = 20
    const width = 100
    const height = 100
    const padding = 10

    const dataPoints = Array.from({ length: points }, (_, i) => {
      const x = (i / (points - 1)) * (width - padding * 2) + padding
      const y = Math.sin(i * 0.5) * 15 + Math.random() * 20 + 30
      return { x, y: height - y - padding }
    })

    const pathData = dataPoints.reduce((path, point, i) => {
      if (i === 0) {
        return `M ${point.x},${point.y}`
      }
      const prev = dataPoints[i - 1]
      const cpx1 = prev.x + (point.x - prev.x) / 3
      const cpx2 = prev.x + ((point.x - prev.x) * 2) / 3
      return `${path} C ${cpx1},${prev.y} ${cpx2},${point.y} ${point.x},${point.y}`
    }, "")

    const areaPath = `${pathData} L ${width - padding},${height - padding} L ${padding},${
      height - padding
    } Z`

    return (
      <LoadingSkeleton>
        <SkeletonAreaPath viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
          <defs>
            <linearGradient id="skeletonGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={theme.colors.grey} stopOpacity={0.6} />
              <stop offset="95%" stopColor={theme.colors.greyLight} stopOpacity={0.1} />
            </linearGradient>
          </defs>
          <path
            d={areaPath}
            fill="url(#skeletonGradient)"
            stroke={theme.colors.grey}
            strokeWidth="0.5"
            className="skeleton-gradient"
          />
        </SkeletonAreaPath>
        <SkeletonAxis>
          {Array.from({ length: 6 }, (_, i) => (
            <SkeletonLabel key={i} />
          ))}
        </SkeletonAxis>
      </LoadingSkeleton>
    )
  }

  return (
    <Wrapper>
      <Header>
        <Title>Checkout Rate Over Time</Title>
        {timeRangeDays > 0 && (
          <ToggleContainer>
            <ToggleButton
              $active={interval === "hourly"}
              onClick={() => onIntervalChange("hourly")}
            >
              Hourly
            </ToggleButton>
            <ToggleButton $active={interval === "daily"} onClick={() => onIntervalChange("daily")}>
              Daily
            </ToggleButton>
          </ToggleContainer>
        )}
      </Header>
      {loading ? (
        renderSkeletonChart()
      ) : error ? (
        <EmptyState>Error loading chart data</EmptyState>
      ) : chartData.length === 0 ? (
        <EmptyState>No data available</EmptyState>
      ) : (
        <ChartWrapper $isLoading={loading}>
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chartDataWithRate}
                margin={{
                  top: 10,
                  right: 30,
                  left: 0,
                  bottom: 0
                }}
              >
                <defs>
                  <linearGradient id="colorCheckoutRate" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={theme.colors.turquoiseDark} stopOpacity={0.8} />
                    <stop offset="100%" stopColor={theme.colors.turquoiseDark} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="displayLabel"
                  tick={{ fontSize: 10 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="checkoutRate"
                  stroke={theme.colors.turquoiseDark}
                  fillOpacity={1}
                  fill="url(#colorCheckoutRate)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartContainer>
        </ChartWrapper>
      )}
    </Wrapper>
  )
}
